import { Panel } from '@bcecloud/portal-ui';

export interface ActionCardProps {
  /** The card heading — the thing this action is about ("Contract"). */
  readonly title: string;
  /** One line saying what the card does or shows. */
  readonly description?: string;
  /** A pill-style status shown beside the title (e.g. "Not started"). */
  readonly status?: string;
  /** The card body — the action(s) or an empty state. */
  readonly children: React.ReactNode;
}

/**
 * One titled card in the embed's action list. The embed is a deliberately
 * minimal surface (it renders inside a Salesforce iframe), so it uses the
 * standard `Panel` for chrome and portal typography rather than a new
 * design-system concept.
 *
 * @param props - Title, optional description/status, and the card body.
 * @returns The action card.
 */
export function ActionCard({ title, description, status, children }: ActionCardProps): React.ReactElement {
  return (
    <Panel className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-portal-card-title font-bold text-portal-ink">{title}</h2>
          {description === undefined ? null : (
            <p className="mt-0.5 text-portal-small text-portal-muted">{description}</p>
          )}
        </div>
        {status === undefined ? null : (
          <span className="shrink-0 rounded-full bg-portal-sky px-2 py-0.5 text-portal-micro text-brand">
            {status}
          </span>
        )}
      </div>
      <div className="mt-3">{children}</div>
    </Panel>
  );
}
